<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 text-gray-900 relative overflow-hidden mt-1">
    <!-- Background Pattern -->
    <BackgroundPattern />
    
    <!-- Privacy Banner Modal -->
    <PrivacyBanner :show="showPrivacyBanner" @accept="acceptPrivacy" />
    
    <!-- Global Error Alert -->
    <GlobalError :message="globalError" :type="globalErrorType" @close="clearGlobalError" />
    
    <!-- PDF Preview Modal -->
    <PDFPreview
      :show="showPDFPreview"
      :loading="pdfLoading"
      :error="pdfError"
      :submitting="submittingPDF"
      :incidentId="incidentId"
      :formSummary="formSummary"
      @close="closePDFPreview"
      @confirm="confirmPDFSubmission"
      @download="downloadPDF"
    />
    
    <!-- Submission Progress Modal -->
    <SubmissionProgress
      :show="showSubmissionProgress"
      :title="submissionProgressTitle"
      :message="submissionProgressMessage"
      :progress="submissionProgress"
      :steps="submissionSteps"
    />
    
    <!-- Honeypot -->
    <input type="text" v-model="honeypot" class="hidden" tabindex="-1" autocomplete="off" aria-hidden="true" />

    <div class="relative z-10 p-4 sm:p-6 lg:p-8 mt-5">
      <!-- Header -->
      <div class="max-w-3xl mx-auto mb-4 text-center">
        <div class="inline-flex items-center gap-3 mb-1">
          <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 class="text-3xl md:text-4xl font-bold text-gray-900">
            {{ $t('incidentForm.title') }} <span class="text-blue-600">{{ $t('incidentForm.titleHighlight') }}</span>
          </h1>
        </div>
        <p class="text-gray-600 text-lg">{{ $t('incidentForm.subtitle') }}</p>
      </div>
      
      <!-- Progress Bar -->
      <ProgressBar v-if="currentPage >= -1" :steps="steps" :currentPage="currentPage" :totalSteps="7" />

      <!-- Main Form Container with transitions - RESPONSIVE WIDTH -->
      <div class="form-container-wrapper">
        <transition :name="transitionName" mode="out-in">
          <AuthMethodSelection v-if="currentPage === -2" :selectedMethod="selectedAuthMethod" @select="selectAuthMethod" @continue="proceedToContactInput" :key="-2" />
          
          <ContactInput
            v-else-if="currentPage === -1"
            :authMethod="selectedAuthMethod"
            :email="form.email"
            :whatsappLocal="whatsappLocal"
            :selectedCountry="selectedCountry.code"
            :errors="errors"
            :loading="loading"
            :canSubmit="canSendOTP"
            :rateLimitMessage="rateLimitMessage"
            @update:email="form.email = $event"
            @update:whatsappLocal="whatsappLocal = $event"
            @update:whatsappFull="form.whatsapp = $event"
            @validate-email="validateEmailInput"
            @validate-phone="validateWhatsAppInput"
            @country-change="onCountryChange"
            @submit="handleInitialSubmit"
            @back="prevPage"
            :key="-1"
          >
            <template #captcha>
              <div v-show="!carriedToken && !emailFallbackSessionUuid" id="incident-recaptcha-widget"></div>
            </template>
          </ContactInput>
          
          
          <OTPVerification v-else-if="currentPage === 0" :authMethod="selectedAuthMethod" :contactValue="selectedAuthMethod === 'email' ? form.email : form.whatsapp" :otpCountdown="otpCountdown" :resendCountdown="resendCountdown" :canResendOTP="canResendOTP" :resendLoading="resendLoading" :loading="loading" :error="errors.otp" @update:otp="form.otp = $event" @submit="handleOTPSubmit" @resend="resendOTP" @back="prevPage" :key="0" />
          
          <PersonalInfo v-else-if="currentPage === 1" :form="form" :errors="errors" :loading="loading" :authMethod="selectedAuthMethod" @update:field="updateFormField" @submit="handleStep1Submit" @back="prevPage" :key="1" />
          
          <AddressDocuments 
            v-else-if="currentPage === 2" 
            :form="form" 
            :errors="errors" 
            :loading="loading" 
            :documentType="documentType" 
            :idPhoto1Url="id_photo1_url || restoredIdPhotoUrl" 
            :idPhoto2Url="id_photo2_url" 
            :passportPreviewUrl="passportPreviewUrl || restoredPassportUrl"
            :sessionUuid="session_uuid"
            :hasRestoredPhoto="hasRestoredIdPhoto || hasRestoredPassport"
            @update:field="updateFormField" 
            @update:documentType="documentType = $event" 
            @upload-front="handleFrontPhotoUpload" 
            @upload-back="handleBackPhotoUpload" 
            @upload-passport="handlePassportUpload" 
            @submit="handleStep2Complete" 
            @back="prevPage" 
            :key="2" 
          />
          
          <IncidentDetails v-else-if="currentPage === 3" :form="form" :errors="errors" :loading="loading" :incidents="incidents" @update:field="updateFormField" @toggle-incident="toggleIncident" @submit="handleStep3Submit" @back="prevPage" :key="3" />
          
          <EvidenceUpload v-else-if="currentPage === 4" :session-uuid="session_uuid" :form="form" :evidence-files="evidenceFiles" :initial-incident-id="incidentId" @back="prevPage" @submit="handleEvidenceSubmitSuccess" @update:evidence-files="evidenceFiles = $event" :key="4" />
          
          <SuccessPage v-else-if="currentPage === 5" :showDownloadSection="showDownloadSection" :downloadingPDF="downloadingPDF" @download-pdf="downloadPDF" @reset="resetForm" :key="5" />
        </transition>
      </div>
    </div>
  </div>
  
  <!-- Footer -->
  <div class="border-t border-gray-200 bg-white w-full">
    <div class="container mx-auto px-4 py-4">
      <div class="flex flex-col md:flex-row justify-between items-center gap-3">
        <p class="text-xs text-gray-600">&copy; 2024 ANTIC CIRT - Cyber Incident Response Team</p>
        <div class="flex items-center gap-4 text-xs text-gray-500">
          <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('incidentForm.privacy') }}</a>
          <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('incidentForm.terms') }}</a>
          <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('incidentForm.help') }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getAPI from '@/axios-api';
import JSZip from 'jszip';
import { sanitizeInput, isValidEmail, generateSecureFileName, RateLimiter, sanitizeFileName } from '@/utils/security-utils';
import { executeRecaptcha, loadRecaptcha, renderRecaptcha, resetRecaptcha, getRecaptchaResponse } from '@/utils/recaptcha';
import { findByCode } from '@/utils/countries';

// UI Components
import BackgroundPattern from './ui/BackgroundPattern.vue';
import ProgressBar from './ui/ProgressBar.vue';
import GlobalError from './ui/GlobalError.vue';

// Modals
import PrivacyBanner from './modals/PrivacyBanner.vue';
import PDFPreview from './modals/PDFPreview.vue';
import SubmissionProgress from './modals/SubmissionProgress.vue';

// Steps
import AuthMethodSelection from './steps/AuthMethodSelection.vue';
import ContactInput from './steps/ContactInput.vue';
import OTPVerification from './steps/OTPVerification.vue';
import PersonalInfo from './steps/PersonalInfo.vue';
import AddressDocuments from './steps/AddressDocuments.vue';
import IncidentDetails from './steps/IncidentDetails.vue';
import EvidenceUpload from './steps/EvidenceUpload.vue';
import SuccessPage from './steps/SuccessPage.vue';

export default {
  name: 'IncidentForm',
  components: {
    BackgroundPattern, ProgressBar, GlobalError,
    PrivacyBanner, PDFPreview, SubmissionProgress,
    AuthMethodSelection, ContactInput, OTPVerification,
    PersonalInfo, AddressDocuments, IncidentDetails,
    EvidenceUpload, SuccessPage
  },
  data() {
    return {
      currentPage: -2,
      transitionDirection: 'forward',
      loading: false,
      documentType: 'id_card',
      passport: null,
      passportPreviewUrl: null,
      restoredIdPhotoUrl: null,
      restoredPassportUrl: null,
      hasRestoredIdPhoto: false,
      hasRestoredPassport: false,
      honeypot: '',
      otpRateLimiter: new RateLimiter(3, 300000),
      recaptchaWidgetId: null,
      recaptchaToken: '',
      carriedToken: '',
      emailFallbackSessionUuid: '',
      selectedAuthMethod: null,
      showPrivacyBanner: true,
      showPDFPreview: false,
      showSubmissionProgress: false,
      submissionProgress: 0,
      submissionProgressTitle: 'Processing Submission',
      submissionProgressMessage: 'Please wait...',
      submissionSteps: [],
      otpCountdown: 300,
      otpTimer: null,
      resendCountdown: 60,
      resendTimer: null,
      canResendOTP: false,
      resendLoading: false,
      incidentId: null,
      form: {
        email: '', whatsapp: '', otp: '', name: '', phone_number: '',
        profession: '', id_type: '', id_number: '', residence_address: '',
        residence_town: '', incident_type: '', incident_date: '',
        incident_description: '', otherIncident: '', owner_phone_number: '', id_photo1: null, id_photo2: null,
      },
      id_photo1_url: null,
      id_photo2_url: null,
      errors: {},
      session_uuid: null,
      sessionToken: '',
      evidenceFiles: [],
      globalError: '',
      globalErrorType: 'error',
      rateLimitMessage: '',
      selectedCountry: { name: 'Cameroon', dial_code: '237', code: 'CM' },
      whatsappLocal: '',
      generatingPDF: false,
      submittingPDF: false,
      downloadingPDF: false,
      pdfLoading: false,
      pdfError: null,
      accessToken: null,
      reportReference: '',
      showDownloadSection: false
    };
  },
  computed: {
    transitionName() {
      return this.transitionDirection === 'forward' ? 'slide-left' : 'slide-right';
    },
    steps() {
      return [
        this.$t('incidentForm.steps.verify'),
        this.$t('incidentForm.steps.personal'),
        this.$t('incidentForm.steps.documents'),
        this.$t('incidentForm.steps.incident'),
        this.$t('incidentForm.steps.evidence'),
        this.$t('incidentForm.steps.preview'),
        this.$t('incidentForm.steps.complete'),
      ];
    },
    incidents() {
      return [
        this.$t('incidentForm.crimes.defacement'),
        this.$t('incidentForm.crimes.hijacking'),
        this.$t('incidentForm.crimes.itheft'),
        this.$t('incidentForm.crimes.malicious'),
        this.$t('incidentForm.crimes.blackmail'),
        this.$t('incidentForm.crimes.cyberH'),
        this.$t('incidentForm.crimes.scamming'),
        this.$t('incidentForm.crimes.malware'),
        this.$t('incidentForm.crimes.Ptheft'),
        this.$t('incidentForm.crimes.other'),
      ];
    },
    canSendOTP() {
      if (this.selectedAuthMethod === 'email') {
        return this.form.email && !this.errors.email;
      } else if (this.selectedAuthMethod === 'phone') {
        return this.form.whatsapp && !this.errors.whatsapp;
      }
      return false;
    },
    formSummary() {
      return {
        name: this.form.name || 'N/A',
        incidentType: this.form.incident_type || 'N/A',
        date: this.form.incident_date || 'N/A',
        location: this.form.residence_town || 'N/A'
      };
    }
  },
  methods: {
    // Navigation
    nextPage() {
      this.transitionDirection = 'forward';
      this.currentPage++;
    },
    prevPage() {
      this.transitionDirection = 'backward';
      if (this.currentPage > -2) {
        this.currentPage--;
        this.globalError = '';
      }
    },
    
    // Privacy
    acceptPrivacy() {
      this.showPrivacyBanner = false;
      localStorage.setItem('privacyAccepted', 'true');
    },
    
    // Auth Method
    selectAuthMethod(method) {
      this.selectedAuthMethod = method;
      if (method === 'email') {
        this.form.whatsapp = '';
        this.whatsappLocal = '';
        this.errors.whatsapp = '';
      } else {
        this.errors.email = '';
      }
    },
    proceedToContactInput() {
      if (this.selectedAuthMethod) {
        this.nextPage();
        // Only render the in-form checkbox if we don't already hold a token
        // carried from the RecaptchaGate.
        if (!this.carriedToken) {
          this.$nextTick(() => this.renderIncidentRecaptcha());
        }
      }
    },

    async renderIncidentRecaptcha(attempt = 0) {
      try {
        await loadRecaptcha();
        const container = document.getElementById('incident-recaptcha-widget');
        if (!container) {
          // Element may not be mounted yet (transition/slot). Retry briefly.
          if (attempt < 10) {
            setTimeout(() => this.renderIncidentRecaptcha(attempt + 1), 150);
          }
          return;
        }
        container.innerHTML = '';
        this.recaptchaWidgetId = null;
        this.recaptchaToken = '';
        this.recaptchaWidgetId = await renderRecaptcha(
          'incident-recaptcha-widget',
          (token) => { this.recaptchaToken = token || ''; },
          () => { this.recaptchaToken = ''; }
        );
      } catch (e) {
        // If reCAPTCHA can't load, leave token empty; backend will reject the send.
        this.rateLimitMessage = 'Could not load CAPTCHA. Please refresh and try again.';
      }
    },

    resetIncidentRecaptcha() {
      this.recaptchaToken = '';
      if (this.recaptchaWidgetId !== null && this.recaptchaWidgetId !== undefined) {
        resetRecaptcha(this.recaptchaWidgetId);
      }
    },
    
    // Form field update
    updateFormField({ field, value }) {
      this.form[field] = value;
      // Clear error when user starts typing
      if (this.errors[field]) {
        this.errors[field] = '';
      }
    },
    
    // Error handling
    clearGlobalError() { this.globalError = ''; },
    showGlobalError(message, duration = 5000, type = 'error') {
      this.globalError = message;
      this.globalErrorType = type;
      setTimeout(() => { if (this.globalError === message) this.globalError = ''; }, duration);
    },
    handleAPIError(error) {
      let message = 'An error occurred. Please try again.';
      if (error.response) {
        if (error.response.status === 429) {
          message = error.response.data?.error
            || 'You have made too many attempts. Please wait a few minutes and try again.';
        } else if (error.response.status === 403) {
          message = error.response.data?.error
            || 'Verification required. Please complete the security check and try again.';
        } else if (error.response.status === 503) {
          message = 'Service temporarily unavailable. Please try again shortly.';
        } else if (error.response.status === 500) {
          message = 'A server error occurred. Please try again.';
        } else if (error.response.data?.error) {
          message = error.response.data.error;
        }
      } else if (error.request) {
        message = 'Network error. Please check your connection and try again.';
      }
      this.showGlobalError(message);
    },
    checkHoneypot() { if (this.honeypot) throw new Error('Invalid request'); },
    
    // Validations
    validateEmailInput() {
      const email = sanitizeInput(this.form.email);
      this.form.email = email;
      if (!email) { this.errors.email = 'Email is required'; return false; }
      if (!isValidEmail(email)) { this.errors.email = 'Please enter a valid email address'; return false; }
      this.errors.email = '';
      return true;
    },
    validateWhatsAppInput() {
      const local = this.whatsappLocal.replace(/\D/g, '');
      this.whatsappLocal = local;
      if (!local) { this.errors.whatsapp = 'WhatsApp number is required'; return false; }
      this.formatWhatsappNumber();
      if (!this.isValidWhatsappNumber(this.form.whatsapp, this.selectedCountry.dial_code)) {
        this.errors.whatsapp = `Invalid number format for ${this.selectedCountry.name}`;
        return false;
      }
      this.errors.whatsapp = '';
      return true;
    },
    formatWhatsappNumber() {
      let phone = this.whatsappLocal.replace(/\D/g, '');
      if (phone.startsWith(this.selectedCountry.dial_code)) phone = phone.slice(this.selectedCountry.dial_code.length);
      if (phone.startsWith('0')) phone = phone.slice(1);
      this.form.whatsapp = this.selectedCountry.dial_code + phone;
    },
    isValidWhatsappNumber(phone, dial_code) {
      if (!phone) return false;
      if (dial_code === '237') return /^6\d{8}$/.test(phone.slice(dial_code.length));
      return /^\d{7,15}$/.test(phone.slice(dial_code.length));
    },
    onCountryChange(code) {
      this.selectedCountry = findByCode(code) || { name: 'Cameroon', dial_code: '237', code: 'CM' };
      this.whatsappLocal = '';
      this.form.whatsapp = '';
      this.errors.whatsapp = '';
    },
    
    // Timer functions
    startOTPCountdown() {
      this.otpCountdown = 300;
      this.clearOTPTimer();
      this.otpTimer = setInterval(() => {
        this.otpCountdown--;
        if (this.otpCountdown <= 0) {
          this.clearOTPTimer();
          this.showGlobalError('Verification code has expired. Please request a new one.');
        }
      }, 1000);
    },
    startResendCountdown() {
      this.resendCountdown = 60;
      this.canResendOTP = false;
      this.clearResendTimer();
      this.resendTimer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) { this.canResendOTP = true; this.clearResendTimer(); }
      }, 1000);
    },
    clearOTPTimer() { if (this.otpTimer) { clearInterval(this.otpTimer); this.otpTimer = null; } },
    clearResendTimer() { if (this.resendTimer) { clearInterval(this.resendTimer); this.resendTimer = null; } },
    
    // Form submission handlers
    async handleInitialSubmit() {
      try {
        this.checkHoneypot();
        const isValid = this.selectedAuthMethod === 'email' ? this.validateEmailInput() : this.validateWhatsAppInput();
        if (!isValid) return;
        if (!this.otpRateLimiter.canMakeRequest()) {
          const remainingTime = Math.ceil(this.otpRateLimiter.getRemainingTime() / 1000 / 60);
          this.rateLimitMessage = `Too many requests. Please wait ${remainingTime} minute(s).`;
          return;
        }
        // Email fallback after a WhatsApp failure: the session already passed
        // CAPTCHA, so we send its id and DO NOT require a new token.
        const isEmailFallback = this.selectedAuthMethod === 'email' && !!this.emailFallbackSessionUuid;

        let token = '';
        if (!isEmailFallback) {
          // Token priority: the one carried from the gate, else the in-form checkbox.
          token = this.carriedToken || this.recaptchaToken || getRecaptchaResponse(this.recaptchaWidgetId);
          if (!token) {
            this.rateLimitMessage = 'Please complete the CAPTCHA before continuing.';
            if (!this.recaptchaWidgetId) this.$nextTick(() => this.renderIncidentRecaptcha());
            return;
          }
        }
        this.loading = true;
        const payload = {};
        if (isEmailFallback) {
          payload.fallback_session_uuid = this.emailFallbackSessionUuid;
        } else {
          payload.recaptcha_token = token;
        }
        if (this.selectedAuthMethod === 'email') {
          payload.email = sanitizeInput(this.form.email);
          payload.whatsapp = '';
        } else {
          this.formatWhatsappNumber();
          payload.email = '';
          payload.whatsapp = this.selectedCountry.dial_code === '237' && /^6\d{8}$/.test(this.form.whatsapp.slice(3))
            ? [this.form.whatsapp, '237' + this.form.whatsapp.slice(4)]
            : [this.form.whatsapp];
        }
        await getAPI.post('/api/start-session/', payload, { timeout: 240000 });
        this.otpRateLimiter.recordRequest();
        this.carriedToken = '';
        this.emailFallbackSessionUuid = '';
        if (!isEmailFallback) this.resetIncidentRecaptcha();
        this.nextPage();
        this.rateLimitMessage = '';
        this.startOTPCountdown();
        this.startResendCountdown();
      } catch (error) {
        // A carried token may have expired or been rejected. Drop it and show
        // the in-form checkbox so the user can solve a fresh one without being stuck.
        const hadCarried = !!this.carriedToken;
        this.carriedToken = '';
        this.resetIncidentRecaptcha();
        if (hadCarried) {
          this.$nextTick(() => this.renderIncidentRecaptcha());
        }

        // WhatsApp is down: the backend asks us to offer the email channel
        // instead of leaving the user stuck. Switch to email entry on the
        // same step, keep any session, and prompt the user.
        const data = error.response && error.response.data;
        if (data && (data.whatsapp_unavailable || data.offer_email_fallback)) {
          // WhatsApp is down. Switch to email on the SAME session, which has
          // already passed CAPTCHA — so we do NOT re-render or re-ask CAPTCHA.
          if (data.session_uuid) {
            this.emailFallbackSessionUuid = data.session_uuid;
            this.session_uuid = data.session_uuid;
            try { localStorage.setItem('session_uuid', data.session_uuid); } catch (e) { /* ignore */ }
          }
          this.selectedAuthMethod = 'email';
          this.form.email = '';
          this.errors.email = '';
          // Show a clear, explicit notice (this must survive, so set it last
          // and do not trigger any CAPTCHA path that could overwrite it).
          this.rateLimitMessage = this.$t('contactInput.whatsappDownUseEmail');
          this.showGlobalError(this.$t('contactInput.whatsappDownUseEmail'), 9000, 'error');
          return;
        }

        this.handleAPIError(error);
      }
      finally { this.loading = false; }
    },
    
    async handleOTPSubmit() {
      try {
        this.checkHoneypot();
        if (this.form.otp.length !== 6) { this.errors.otp = 'Verification code must be 6 digits'; return; }
        this.loading = true;
        const payload = { otp: this.form.otp };
        if (this.selectedAuthMethod === 'email') { payload.email = sanitizeInput(this.form.email); payload.whatsappNumber = ''; }
        else { payload.whatsappNumber = this.form.whatsapp; payload.email = ''; }
        const response = await getAPI.post('/api/verify-otp/', payload, { timeout: 240000 });
        this.session_uuid = response.data.session_uuid;

        // Ownership token: required on every subsequent protected call.
        // Set as a default header so all step/evidence/pdf/resume calls carry it.
        if (response.data.session_token) {
          this.sessionToken = response.data.session_token;
          getAPI.defaults.headers.common['X-Session-Token'] = response.data.session_token;
          try { sessionStorage.setItem('session_token', response.data.session_token); } catch (e) { /* ignore */ }
        }

        // Store session UUID in localStorage for backup access
        if (this.session_uuid) {
          localStorage.setItem('session_uuid', this.session_uuid);
        }
        
        // Store incident_id if returned
        if (response.data.incident_id) {
          this.incidentId = response.data.incident_id;
          localStorage.setItem('incident_id', response.data.incident_id);
        }
        
        this.errors.otp = '';
        this.clearOTPTimer();
        this.clearResendTimer();
        
        if (this.selectedAuthMethod === 'phone' && this.form.whatsapp && !this.form.phone_number) {
          this.form.phone_number = this.form.whatsapp;
        }
        
        // Check if there's existing session data to restore
        const hasExistingData = response.data.has_existing_data || 
                                (response.data.data && Object.keys(response.data.data).length > 0) ||
                                response.data.incident_id;
        
        if (hasExistingData) {
          this.restoreSessionData(response.data);
          const targetStep = response.data.current_step || 1;
          this.transitionDirection = 'forward';
          this.currentPage = targetStep;
          this.showGlobalError('Welcome back! Your progress has been restored.', 3000, 'success');
        } else {
          this.nextPage();
        }
      } catch (error) { this.errors.otp = 'Invalid or expired verification code'; this.handleAPIError(error); }
      finally { this.loading = false; }
    },
    
    restoreSessionData(responseData) {
      const data = responseData.data || {};
      const incident = responseData.incident || {};
      
      // Field mappings - try multiple source names for each form field
      const fieldMappings = {
        name: ['name', 'full_name'],
        email: ['email'],
        phone_number: ['phone_number', 'phone', 'whatsapp'],
        profession: ['profession', 'occupation'],
        id_type: ['id_type', 'document_type'],
        id_number: ['id_number', 'document_number'],
        residence_address: ['residence_address', 'address'],
        residence_town: ['residence_town', 'town', 'city'],
        incident_type: ['incident_type', 'type'],
        incident_date: ['incident_date', 'date'],
        incident_description: ['incident_description', 'description'],
        otherIncident: ['otherIncident', 'other_incident', 'custom_incident']
      };
      
      // Restore each form field
      Object.entries(fieldMappings).forEach(([formField, sourceFields]) => {
        for (const sourceField of sourceFields) {
          let value = data[sourceField] || incident[sourceField];
          if (value && value !== '') {
            if (formField === 'incident_type' && Array.isArray(value)) {
              value = value.join(', ');
            }
            this.form[formField] = value;
            break;
          }
        }
      });
      
      // Restore document type
      const docType = incident.document_type || data.document_type;
      if (docType) {
        this.documentType = docType === 'passport' ? 'passport' : 'id_card';
      }
      
      // Store ID photo URLs for reference (files already on server)
      if (incident.id_photo_url) {
        this.restoredIdPhotoUrl = incident.id_photo_url;
        this.hasRestoredIdPhoto = true;
      }
      if (incident.passport_file_url) {
        this.restoredPassportUrl = incident.passport_file_url;
        this.hasRestoredPassport = true;
      }
      

    },
    
    async resendOTP() {
      if (!this.canResendOTP || this.resendLoading) return;
      // start-session now requires a fresh CAPTCHA token. A v2 token is
      // single-use, so send the user back to the contact step to re-solve.
      const token = this.recaptchaToken || getRecaptchaResponse(this.recaptchaWidgetId);
      if (!token) {
        this.transitionDirection = 'back';
        this.currentPage = -1;
        this.$nextTick(() => this.renderIncidentRecaptcha());
        this.showGlobalError('Please complete the CAPTCHA again to resend a code.', 4000, 'error');
        return;
      }
      this.resendLoading = true;
      try {
        const payload = { recaptcha_token: token };
        if (this.selectedAuthMethod === 'email') { payload.email = sanitizeInput(this.form.email); payload.whatsapp = ''; }
        else { this.formatWhatsappNumber(); payload.email = ''; payload.whatsapp = [this.form.whatsapp]; }
        await getAPI.post('/api/start-session/', payload, { timeout: 30000 });
        this.resetIncidentRecaptcha();
        this.startOTPCountdown();
        this.startResendCountdown();
        this.showGlobalError('New verification code sent!', 3000, 'success');
      } catch (error) { this.resetIncidentRecaptcha(); this.handleAPIError(error); }
      finally { this.resendLoading = false; }
    },
    
    async handleStep1Submit() {
      try {
        this.checkHoneypot();
        this.loading = true;
        const formData = new FormData();
        formData.append('session_uuid', this.session_uuid);
        formData.append('name', sanitizeInput(this.form.name));
        formData.append('email', sanitizeInput(this.form.email));
        formData.append('phone_number', sanitizeInput(this.form.phone_number_full || this.form.phone_number));
        formData.append('profession', sanitizeInput(this.form.profession));
        formData.append('id_type', this.form.id_type);
        formData.append('id_number', sanitizeInput(this.form.id_number));
        if (this.form.owner_phone_number) {
          formData.append('owner_phone_number', sanitizeInput(this.form.owner_phone_number));
        }
        formData.append('step', 1);
        await getAPI.post('/api/step/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 240000 });
        this.nextPage();
      } catch (error) { this.handleAPIError(error); }
      finally { this.loading = false; }
    },
    
    // Handle Step 2 completion from AddressDocuments component
    handleStep2Complete(result) {
      if (result && result.success) {
        this.nextPage();
      }
    },
    
    async handleStep3Submit() {
      try {
        this.checkHoneypot();
        if (!this.form.incident_type) {
          this.errors.incident_type = 'Please select at least one incident type';
          return;
        }
        this.loading = true;
        const formData = new FormData();
        let incident_type = this.form.incident_type;
        const otherLabel = this.$t('incidentForm.crimes.other');
        if (this.form.incident_type.includes(otherLabel) && this.form.otherIncident) {
          incident_type += `: ${sanitizeInput(this.form.otherIncident)}`;
        }
        formData.append('session_uuid', this.session_uuid);
        formData.append('incident_type', sanitizeInput(incident_type));
        formData.append('incident_date', this.form.incident_date);
        formData.append('incident_description', sanitizeInput(this.form.incident_description));
        formData.append('step', 3);
        await getAPI.post('/api/step/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 240000 });
        this.nextPage();
      } catch (error) { this.handleAPIError(error); }
      finally { this.loading = false; }
    },
    
    toggleIncident(incident) {
      let types = this.form.incident_type.split(',').map(item => item.trim()).filter(Boolean);
      if (types.includes(incident)) types = types.filter(type => type !== incident);
      else types.push(incident);
      this.form.incident_type = types.join(', ');
      this.errors.incident_type = '';
    },
    
    // File uploads (for parent tracking).
    // The child (AddressDocuments) now compresses/converts each image to a
    // safe JPEG and emits the processed File as { processedFile }. We just
    // store it here; the child already handled size and type.
    async handleFrontPhotoUpload(payload) {
      const file = payload && payload.processedFile;
      if (!file) return;
      this.form.id_photo1 = file;
      if (this.id_photo1_url) URL.revokeObjectURL(this.id_photo1_url);
      this.id_photo1_url = URL.createObjectURL(file);
      this.errors.id_photo1 = '';
    },
    async handleBackPhotoUpload(payload) {
      const file = payload && payload.processedFile;
      if (!file) return;
      this.form.id_photo2 = file;
      if (this.id_photo2_url) URL.revokeObjectURL(this.id_photo2_url);
      this.id_photo2_url = URL.createObjectURL(file);
      this.errors.id_photo2 = '';
    },
    async handlePassportUpload(payload) {
      const file = payload && payload.processedFile;
      if (!file) return;
      this.passport = file;
      if (this.passportPreviewUrl) URL.revokeObjectURL(this.passportPreviewUrl);
      this.passportPreviewUrl = file.type.startsWith('image') ? URL.createObjectURL(file) : null;
      this.errors.passport = '';
    },
    processEvidenceFiles(files) {
      const maxSize = 25 * 1024 * 1024;
      let totalSize = this.evidenceFiles.reduce((sum, f) => sum + f.size, 0);
      files.forEach(f => totalSize += f.size);
      if (totalSize > maxSize) { this.errors.evidenceFiles = 'Total file size exceeds 25 MB'; return; }
      if (this.evidenceFiles.length + files.length > 10) { this.errors.evidenceFiles = 'Maximum 10 files allowed'; return; }
      this.errors.evidenceFiles = '';
      this.evidenceFiles = [...this.evidenceFiles, ...files];
    },
    removeEvidenceFile(index) { this.evidenceFiles.splice(index, 1); },
    
    // Handle successful submission from EvidenceUpload component
    handleEvidenceSubmitSuccess(referenceNumber) {
      this.reportReference = referenceNumber || this.reportReference;
      this.currentPage = 5;
      this.showDownloadSection = true;
      this.clearSensitiveData();
      this.showGlobalError('🎉 Incident report submitted successfully!', 5000, 'success');
    },
    
    // PDF Generation and submission
    async generatePDFPreview() {
      this.generatingPDF = true;
      this.pdfLoading = true;
      this.pdfError = null;
      try {
        const response = await getAPI.post('/api/complaint-pdf/generate/', {
          session_uuid: this.session_uuid,
          custom_incident_type: this.form.otherIncident || '',
          location: this.form.residence_town || 'Yaoundé',
          preview: true
        }, { withCredentials: true, timeout: 120000 });
        
        if (!response.data?.success) throw new Error(response.data?.error || 'Failed to generate PDF');
        this.incidentId = response.data.incident_info?.incident_id || response.data.incident_id;
        this.reportReference = response.data.c_reference_id || response.data.reference_id;
        this.accessToken = response.data.incident_info?.access_token || response.data.access_token;
        this.showPDFPreview = true;
        this.showGlobalError('PDF generated successfully!', 3000, 'success');
      } catch (error) {
        this.pdfError = error.message || 'Failed to generate PDF';
        this.showPDFPreview = true;
      } finally {
        this.generatingPDF = false;
        this.pdfLoading = false;
      }
    },
    closePDFPreview() { this.showPDFPreview = false; this.pdfError = null; },
    
    async confirmPDFSubmission() {
      this.submittingPDF = true;
      this.showSubmissionProgress = true;
      this.submissionProgress = 0;
      this.submissionSteps = [
        { label: 'Confirming PDF...', completed: false },
        { label: 'Uploading evidence...', completed: false },
        { label: 'Finalizing submission...', completed: false }
      ];
      
      try {
        let recaptchaToken = null;
        try { recaptchaToken = await executeRecaptcha('antic_complaint_submit'); } catch (e) { /* continue */ }
        
        // Step 1: Confirm PDF
        this.submissionProgress = 20;
        const confirmPayload = {
          incident_id: this.incidentId,
          session_uuid: this.session_uuid,
          access_token: this.accessToken
        };
        if (recaptchaToken) confirmPayload.recaptcha_token = recaptchaToken;
        
        const confirmResponse = await getAPI.post('/api/complaint-pdf/confirm/', confirmPayload, { withCredentials: true, timeout: 60000 });
        
        if (!confirmResponse.data.success) throw new Error(confirmResponse.data.error || 'Failed to confirm PDF');
        this.submissionSteps[0].completed = true;
        this.submissionProgress = 40;
        this.reportReference = confirmResponse.data.c_reference_id;
        
        // Step 2: Upload evidence
        this.submissionProgress = 60;
        const formData = new FormData();
        formData.append('session_uuid', this.session_uuid);
        if (this.evidenceFiles.length > 0) {
          const evidenceZip = new JSZip();
          this.evidenceFiles.forEach(file => evidenceZip.file(generateSecureFileName(file.name), file));
          const zipContent = await evidenceZip.generateAsync({ type: "blob" });
          formData.append('evidence_files', zipContent, `${sanitizeFileName(this.form.name)}_evidence.zip`);
        }
        formData.append('step', 4);
        await getAPI.post('/api/step/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 480000 });
        this.submissionSteps[1].completed = true;
        this.submissionProgress = 80;
        
        // Step 3: Finalize
        this.submissionSteps[2].completed = true;
        this.submissionProgress = 100;
        
        this.closePDFPreview();
        this.showSubmissionProgress = false;
        this.currentPage = 5;
        this.showDownloadSection = true;
        this.clearSensitiveData();
        this.showGlobalError('🎉 Incident report submitted successfully!', 5000, 'success');
      } catch (error) {
        this.showSubmissionProgress = false;
        this.handleAPIError(error);
      } finally {
        this.submittingPDF = false;
      }
    },
    
    async downloadPDF() {
      this.downloadingPDF = true;
      try {
        const params = new URLSearchParams();
        if (this.incidentId) params.append('incident_id', this.incidentId);
        if (this.reportReference) params.append('c_reference_id', this.reportReference);
        if (this.session_uuid) params.append('session_uuid', this.session_uuid);
        
        const response = await getAPI.get(`/api/complaint-pdf/download/?${params}`, { 
          responseType: 'blob', 
          timeout: 60000 
        });
        
        // Always build a locale-aware, name-based filename client-side
        // (the server has no language context): Antic_plainte_de_{name} (fr) /
        // Antic_complaint_of_{name} (en).
        const safeName = (sanitizeFileName(this.form.name || '').replace(/^_+|_+$/g, '') || (this.$i18n.locale === 'fr' ? 'usager' : 'user'));
        const filename = this.$i18n.locale === 'fr'
          ? `Antic_plainte_de_${safeName}.pdf`
          : `Antic_complaint_of_${safeName}.pdf`;
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        this.showGlobalError('PDF download started!', 3000, 'success');
      } catch (error) { 
        this.handleAPIError(error); 
      } finally { 
        this.downloadingPDF = false; 
      }
    },
    
    clearSensitiveData() {
      this.form.otp = '';
      if (this.id_photo1_url) URL.revokeObjectURL(this.id_photo1_url);
      if (this.id_photo2_url) URL.revokeObjectURL(this.id_photo2_url);
      if (this.passportPreviewUrl) URL.revokeObjectURL(this.passportPreviewUrl);
      this.id_photo1_url = null;
      this.id_photo2_url = null;
      this.passportPreviewUrl = null;
      localStorage.removeItem('session_uuid');
    },
    
    resetForm() {
      this.currentPage = -2;
      this.selectedAuthMethod = null;
      this.form = {
        email: '', whatsapp: '', otp: '', name: '', phone_number: '',
        profession: '', id_type: '', id_number: '', residence_address: '',
        residence_town: '', incident_type: '', incident_date: '',
        incident_description: '', otherIncident: '', owner_phone_number: '', id_photo1: null, id_photo2: null,
      };
      this.evidenceFiles = [];
      this.errors = {};
      this.session_uuid = null;
      this.sessionToken = '';
      delete getAPI.defaults.headers.common['X-Session-Token'];
      try { sessionStorage.removeItem('session_token'); } catch (e) { /* ignore */ }
      this.clearSensitiveData();
      this.reportReference = '';
      this.showDownloadSection = false;
    }
  },
  mounted() {
    if (localStorage.getItem('privacyAccepted') === 'true') this.showPrivacyBanner = false;
    loadRecaptcha().catch(() => {});
    // Pick up the single-use token solved at the RecaptchaGate so the user
    // is not asked to solve a second CAPTCHA here.
    try {
      const carried = sessionStorage.getItem('recaptcha_token');
      if (carried) {
        this.carriedToken = carried;
        sessionStorage.removeItem('recaptcha_token');
      }
    } catch (e) { /* sessionStorage unavailable; fall back to in-form checkbox */ }
    // Restore an existing ownership token after a page refresh mid-flow.
    try {
      const savedToken = sessionStorage.getItem('session_token');
      if (savedToken) {
        this.sessionToken = savedToken;
        getAPI.defaults.headers.common['X-Session-Token'] = savedToken;
      }
    } catch (e) { /* ignore */ }
  },
  beforeUnmount() {
    this.clearSensitiveData();
    this.clearOTPTimer();
    this.clearResendTimer();
  }
}
</script>

<style scoped>
/* Responsive form container - Full width on mobile, constrained on larger screens */
.form-container-wrapper {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 0.25rem;
}

@media (min-width: 480px) {
  .form-container-wrapper {
    max-width: 98%;
    padding: 0 0.5rem;
  }
}

@media (min-width: 640px) {
  .form-container-wrapper {
    max-width: 95%;
    padding: 0;
  }
}

@media (min-width: 768px) {
  .form-container-wrapper {
    max-width: 90%;
  }
}

@media (min-width: 1024px) {
  .form-container-wrapper {
    max-width: 80%;
  }
}

/* Page Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>